import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  DATE_RANGE_PRESETS,
  findMatchingPreset,
} from '@components/DateRangeSelector/dateRangePresets';
import CheckIcon from '@mui/icons-material/Check';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import { dateToIso, isoToLocalDate } from '../filterHelpers';
import { useFilterMenuTokens } from '../filterMenuTokens';

interface IsoDateRange {
  start: string | null;
  end: string | null;
}

// The adapter is date-fns, so picker values are always Date at runtime; the
// broader PickerValidDate union only exists in the types.
const toIsoOrNull = (date: unknown): string | null =>
  date instanceof Date ? dateToIso(date) : null;

interface DateRangeEditorPanelProps {
  value: IsoDateRange | undefined;
  /** Preset picked — terminal: the popover applies it and closes the whole menu. */
  onSelectPreset: (range: { start: string; end: string; preset: string }) => void;
  /** Custom bound changed — applied live, menu stays open. */
  onChangeCustom: (range: IsoDateRange) => void;
  autoFocus: boolean;
  /** The calendar popup must clear the editor panel it opens from. */
  popupZIndex: number;
}

/**
 * Preset ladder + custom range editor, modeled on the reports PeriodPopover.
 * Reuses the DateRangeSelector preset logic; the panel layout is local so we
 * don't nest that component's own TextField-and-Popover inside the cascade.
 */
export const DateRangeEditorPanel: React.FC<DateRangeEditorPanelProps> = ({
  value,
  onSelectPreset,
  onChangeCustom,
  autoFocus,
  popupZIndex,
}) => {
  const tokens = useFilterMenuTokens();
  const firstPresetRef = useRef<HTMLButtonElement>(null);

  const currentRange = useMemo(
    () => ({
      start: value?.start ? isoToLocalDate(value.start) : null,
      end: value?.end ? isoToLocalDate(value.end) : null,
    }),
    [value],
  );
  const activePreset = useMemo(() => findMatchingPreset(currentRange), [currentRange]);
  const hasCustomValue = !activePreset && Boolean(currentRange.start || currentRange.end);

  const [customOpen, setCustomOpen] = useState(hasCustomValue);

  useEffect(() => {
    if (autoFocus) firstPresetRef.current?.focus();
  }, [autoFocus]);

  const rowSx = {
    alignItems: 'center',
    bgcolor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    gap: 1,
    height: tokens.rowHeight,
    px: 1.5,
    textAlign: 'left',
    width: '100%',
    '&:hover, &:focus-visible': { bgcolor: tokens.hoverBg },
  } as const;

  return (
    <Box sx={{ py: 0.5 }}>
      {DATE_RANGE_PRESETS.map((preset, index) => (
        <Stack
          component="button"
          type="button"
          direction="row"
          key={preset.value}
          ref={index === 0 ? firstPresetRef : undefined}
          onClick={() => {
            const range = preset.getRange();
            if (!range.start || !range.end) return;
            onSelectPreset({
              start: dateToIso(range.start),
              end: dateToIso(range.end),
              preset: preset.value,
            });
          }}
          sx={rowSx}
        >
          <Box sx={{ width: 16 }}>
            {activePreset?.value === preset.value && (
              <CheckIcon sx={{ color: tokens.ink, fontSize: 14 }} />
            )}
          </Box>
          <Typography sx={{ color: tokens.ink, fontSize: tokens.fontSize }}>
            {preset.label}
          </Typography>
        </Stack>
      ))}

      <Divider sx={{ borderColor: tokens.line, my: 0.5 }} />

      <Stack
        component="button"
        type="button"
        direction="row"
        onClick={() => setCustomOpen(previous => !previous)}
        sx={rowSx}
      >
        <Box sx={{ width: 16 }}>
          {hasCustomValue && <CheckIcon sx={{ color: tokens.ink, fontSize: 14 }} />}
        </Box>
        <Typography sx={{ color: tokens.ink, fontSize: tokens.fontSize }}>Custom range…</Typography>
      </Stack>

      {customOpen && (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Stack spacing={1.5} sx={{ borderTop: `1px solid ${tokens.line}`, mt: 0.5, p: 1.5 }}>
            <DatePicker
              label="Start"
              value={currentRange.start}
              maxDate={currentRange.end ?? undefined}
              onChange={date =>
                onChangeCustom({ start: toIsoOrNull(date), end: value?.end ?? null })
              }
              slotProps={{
                textField: { size: 'small', fullWidth: true },
                popper: { sx: { zIndex: popupZIndex } },
              }}
            />
            <DatePicker
              label="End"
              value={currentRange.end}
              minDate={currentRange.start ?? undefined}
              onChange={date =>
                onChangeCustom({ start: value?.start ?? null, end: toIsoOrNull(date) })
              }
              slotProps={{
                textField: { size: 'small', fullWidth: true },
                popper: { sx: { zIndex: popupZIndex } },
              }}
            />
          </Stack>
        </LocalizationProvider>
      )}
    </Box>
  );
};
